import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { useLocation, useNavigate } from "react-router-dom";
import { Facebook, Instagram, Twitter, Mail, MapPin } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const columns = [
  {
    title: "Riders",
    links: [
      { label: "How it works", href: "#riders" },
      { label: "Get your first ride", href: "#rider-signup" },
      { label: "Download the app", href: "#download" },
    ],
  },
  {
    title: "Drivers",
    links: [
      { label: "Why drive", href: "#drivers" },
      { label: "Start earning", href: "#driver-signup" },
      { label: "Requirements", href: "#drivers" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About EzRueda", href: "#about" },
      { label: "Where your tips go", href: "/where-your-tips-go", route: true },
      { label: "Features", href: "#features" },
      { label: "Contact", href: "mailto:hello@ezrueda.com" },
    ],
  },
];

const socials = [
  { icon: Facebook, label: "Facebook", testid: "social-facebook" },
  { icon: Instagram, label: "Instagram", testid: "social-instagram" },
  { icon: Twitter, label: "Twitter", testid: "social-twitter" },
];

export default function Footer() {
  const lenis = useLenis();
  const location = useLocation();
  const navigate = useNavigate();
  const go = (e, link) => {
    const href = typeof link === "string" ? link : link.href;
    const isRoute = typeof link === "object" && link.route;
    if (href.startsWith("mailto:")) return;
    e.preventDefault();
    if (isRoute) {
      navigate(href);
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/${href}`);
      return;
    }
    if (lenis) lenis.scrollTo(href, { offset: -72, duration: 1.5 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="relative border-t border-white/10 bg-black overflow-hidden" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-5">
            <a href="/" onClick={(e) => go(e, "#top")} className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <img src="/logo.jpg" alt="EzRueda logo" className="w-11 h-11 rounded-xl object-cover ring-1 ring-white/20" />
              <span className="font-display font-semibold text-2xl tracking-tight">
                Ez<span className="text-brand-yellow">Rueda</span>
              </span>
            </a>
            <p className="text-zinc-500 text-sm leading-relaxed max-w-sm mb-8">
              Albany's own rideshare. Big savings for riders, huge earnings for drivers,
              launching soon in the Capital Region.
            </p>
            <div className="space-y-3 text-sm text-zinc-400">
              <a href="mailto:hello@ezrueda.com" className="flex items-center gap-3 hover:text-brand-yellow transition-colors duration-300" data-testid="footer-contact-email">
                <Mail className="w-4 h-4 text-brand-yellow" /> hello@ezrueda.com
              </a>
              <p className="flex items-center gap-3" data-testid="footer-contact-location">
                <MapPin className="w-4 h-4 text-brand-green" /> Albany, New York
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {columns.map((col) => (
              <div key={col.title}>
                <h4 className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-5">
                  {col.title}
                </h4>
                <ul className="space-y-3">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        onClick={(e) => go(e, l)}
                        className="text-sm text-zinc-400 hover:text-white transition-colors duration-300"
                        data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: EASE }}
          className="font-display font-bold uppercase tracking-tight leading-none text-center select-none text-[18vw] lg:text-[11rem] text-outline-yellow opacity-60 pointer-events-none"
          data-testid="footer-wordmark"
          aria-hidden="true"
        >
          EzRueda
        </motion.p>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-white/10 pt-8 mt-4">
          <p className="text-xs text-zinc-600" data-testid="footer-copyright">
            &copy; {new Date().getFullYear()} EzRueda. All rights reserved. Made in Albany, NY.
          </p>
          <div className="flex items-center gap-4">
            {socials.map((s) => (
              <a
                key={s.label}
                href="#"
                onClick={(e) => e.preventDefault()}
                aria-label={s.label}
                data-testid={s.testid}
                className="flex items-center justify-center w-10 h-10 rounded-full border border-white/10 text-zinc-400 hover:text-black hover:bg-brand-yellow hover:border-brand-yellow hover:scale-110 transition-all duration-300"
              >
                <s.icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
